import { useState } from 'react'
import { Link } from 'react-router-dom'
import type { ReportDeal, ReportMetric } from '../../api/types'
import { STAGE_LABEL } from '../pipeline/labels'
import type { Tone } from './tone'
import styles from './Reports.module.css'

/** Enough rows to see the shape of the group without the panel taking the page. */
const INITIAL_ROWS = 5

/**
 * The opportunities behind a count.
 *
 * A lightweight explanation, not a second pipeline view: five columns, no
 * filters, no sorting, no verdict chips. Every column is a fact the backend
 * already produced — nothing here is computed, inferred or scored.
 */
export function MetricEvidence({
  metric,
  tone,
  id,
  labelledBy,
}: {
  metric: ReportMetric
  tone: Tone
  id: string
  labelledBy: string
}) {
  // Remounted per metric by a `key` in ReportSection, so this resets on its own
  // — opening a 40-row metric after a 6-row one must not inherit "show all".
  const [expanded, setExpanded] = useState(false)

  const rows = expanded ? metric.deals : metric.deals.slice(0, INITIAL_ROWS)
  const hidden = metric.deals.length - rows.length

  return (
    <div className={styles.panel} id={id}>
      <div className={styles.panelHead}>
        <h3 className={styles.panelTitle} data-tone={tone} id={`${id}-title`}>
          {metric.sentence}
        </h3>
      </div>

      <div className={styles.tableScroll}>
        <table className={styles.table} aria-labelledby={`${id}-title`} aria-describedby={labelledBy}>
          <thead>
            <tr>
              <th scope="col">Opportunity</th>
              <th scope="col">Stage</th>
              <th scope="col">Why it is here</th>
              <th scope="col">Last analysed</th>
              <th scope="col">
                <span className="dp-sr-only">Open deal</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((deal) => (
              <tr key={deal.deal_id}>
                <td className={styles.company}>{deal.company_name}</td>
                <td className={styles.stage}>{STAGE_LABEL[deal.stage]}</td>
                <td className={styles.why}>
                  {deal.detail ?? metric.label}
                  {deal.quote ? <span className={styles.whyQuote}>“{deal.quote}”</span> : null}
                </td>
                <td className={styles.analysed}>{lastAnalysed(deal)}</td>
                <td>
                  <Link to={`/deals/${deal.deal_id}`} className={styles.openDeal}>
                    Open deal
                    <Arrow />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {hidden > 0 ? (
        <button type="button" className={styles.showMore} onClick={() => setExpanded(true)}>
          <Chevron />
          Show {hidden} more
        </button>
      ) : null}
    </div>
  )
}

/**
 * The date of the last analysed call, phrased the way someone reads a briefing.
 * `days_since_last_call` is the backend's own arithmetic; the date is only
 * formatted here, never recomputed.
 */
function lastAnalysed(deal: ReportDeal) {
  const days = deal.days_since_last_call
  if (days === 0) return 'Today'
  if (days === 1) return 'Yesterday'
  if (deal.last_call_at) {
    return new Date(deal.last_call_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
  }
  return days === null ? 'Not recorded' : `${days} days ago`
}

const Arrow = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 12h15" />
    <path d="m13 6 6 6-6 6" />
  </svg>
)

const Chevron = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m6 9 6 6 6-6" />
  </svg>
)
